export * from "./common";

export enum BrandName {
    EuroFinans = "EuroFinans",
    PrioritetFinans = "PrioritetFinans",
    FinanceLink = "FinanceLink",
}

export enum EIdProvider {
    SeBankId = "seBankID",
    FiTupas = "fiTupas",
}

export type AppDataMaintenance = {
    loginMessage: string;
    loginDisabled: boolean;
};

export type OpenBankingConfiguration = {
    consentExpiresVerySoonDays: number;
    consentExpiresSoonDays: number;
};

export type AppData = {
    cultureCode: string;
    currencyCode: string;
    brandName: BrandName;
    eIdProvider: EIdProvider;
    tokenRefreshIntervalMinutes: number;
    privacyPolicyUrl?: string;
    maintenance: AppDataMaintenance;
    openBankingConfiguration: OpenBankingConfiguration;
};

export enum AccountPaymentMethodResource {
    Unknown = 0,
    Bban = 1,
    Iban = 2,
    Bankgiro = 3,
    Plusgiro = 4,
}

export type AffiliatedBankResource = {
    finaBankId: number;
    id: string;
    name: string;
};

export enum AuthenticationFlowResource {
    Undefined = 0,
    DecoupledOtherDevice = 1,
    DecoupledSameDevice = 2,
    Redirect = 3,
}

export enum AuthorizationMethodResource {
    Decoupled,
    Redirect,
}

export type BankResource = {
    affiliatedBanks: AffiliatedBankResource[];
    authorizationMethod: AuthorizationMethodResource;
    bic: string;
    finaBankId: number;
    name: string;
    requireAffiliatedBank: boolean;
    requireSocialSecurityNumber: boolean;
};

export type BrandContextResource = {
    acconomyIntegration: boolean;
    businessLoansUrl: string;
    contactEmailAddress: string;
    customerServiceEmailAddress: string;
    deActivatedExportOptions: string[];
    deActiveMenus: string[];
    defaultExportOption: string;
    erpIntegration: boolean;
    fortnoxIntegration: boolean;
    imbox: ImboxResource;
    invoiceEmailAddress: string;
    legalName: string;
    name: string;
    organizationNumber: string;
    phone: string;
    showOCRNumber: boolean;
    supportEmailAddress: string;
    supportedLanguages: string[];
};

export type ImboxResource = {
    widgetId: string;
    paymentInformationArticleId: string;
};

export const enum Browser {
    SAFARI,
    CHROME,
    OTHER,
}

export type ConsentResource = {
    token: string;
    challengeData: string;
};

export const enum PaymentInformationMessageSourceResource {
    Unknown = "Unknown",
    OpenPayments = "OpenPayments",
}

export const enum PaymentInformationMessageTypeResource {
    Unknown = "Unknown",
    Error = "Error",
}

export type PaymentInformationMessageResource = {
    description: string;
    source: PaymentInformationMessageSourceResource;
    translationKey: string;
    type: PaymentInformationMessageTypeResource;
};

export type ConsentStatusResource = {
    challengeData: string;
    message: PaymentInformationMessageResource | null;
    status: StatusResource;
};

export type CreateConsentInputResource = {
    affiliatedBankId?: string;
    authenticationFlow: AuthenticationFlowResource;
    bic: string;
    finaBankId: number;
    socialSecurityNumber?: string;
    userAgent: UserAgentResource;
};

export type EIdAuthenticationFiTupasResource = {
    redirectUrl: string;
};

export enum EIdAuthenticationPurpose {
    Unknown = 0,
    UserSignIn = 1,
    AccountActivation = 2,
    EIdActivation = 3,
}

export type EIdAuthenticationSeBankIdResource = {
    applicationUrl: string;
    callbackUrl: string;
    qrCodeImage: string;
    token: string;
};

export enum EIdAuthenticationStatus {
    Pending = 0,
    Completed = 1,
    Failed = 2,
}

export type EIdAuthenticationStatusResource = {
    qrCodeImage: string;
    status: EIdAuthenticationStatus;
    verificationToken?: string;
};

export const enum OperatingSystems {
    IOS,
    ANDROID,
    OTHER,
}

export enum StatusResource {
    PENDING = "PENDING",
    SUCCEEDED = "SUCCEEDED",
    FAILED = "FAILED",
}

export type TokenRefreshResult = {
    ok: boolean;
    token?: string;
};

export type UserAgentResource = {
    operatingSystem: OperatingSystems;
    browser: Browser;
};

export type Role = {
    name: string;
    id: number;
    isAdmin: boolean;
    isCustomerSupport: boolean;
};

export type Company = {
    id: number;
    organisationNumber: string;
    name: string;
    role: Role;
    userEmail: string;
};

export type User = {
    id: number;
    firstName: string;
    lastName: string;
    fullName: string;
    signInMethod?: number;
    phone?: string;
    companies: Company[];
    activeCompany: Company;
    defaultCompany: Company;
    email?: string;
    socialSecurityNumber?: string;
};

export type UserContext = {
    user: User;
};

const UNKNOWN = "Unknown";
const COMPLETE_AND_ACTIVE = "CompleteAndActive";
const ONBOARDING_IN_PROGRESS = "OnboardingInProgress";
const PENDING_MODIFICATIONS = "PendingModifications";
const INACTIVE = "Inactive";

export const CustomerStatus = {
    UNKNOWN,
    COMPLETE_AND_ACTIVE,
    ONBOARDING_IN_PROGRESS,
    PENDING_MODIFICATIONS,
    INACTIVE,
};

export type CustomerStatuses =
    | typeof UNKNOWN
    | typeof COMPLETE_AND_ACTIVE
    | typeof ONBOARDING_IN_PROGRESS
    | typeof PENDING_MODIFICATIONS
    | typeof INACTIVE;

export type CustomerStatusInfo = {
    status: CustomerStatuses;
    signatoriesCompleted: boolean;
    paymentInformationCompleted: boolean;
    kycCompleted: boolean;
    kycSupported: boolean;
    lastUpdatedKycQuestion?: string;
};

export type CustomerAnnualKycFollowUp = {
    due: boolean;
    dueAt: string;
    passedDue: boolean;
};

export type CustomerContext = {
    activeErpIntegration?: ErpIntegrationResource;
    annualKycFollowUp: CustomerAnnualKycFollowUp;
    bankruptcy: boolean;
    complianceApprovedDate?: string;
    hasActiveContract: boolean;
    isEnabledForCashFlowAnalysis: boolean;
    isInactive: boolean;
    latestInvoiceDate?: string;
    name: string;
    number: string;
    organizationNumber: string;
    serviceId: number;
    status: CustomerStatusInfo;
    useVat: boolean;
};

export type ComplianceSummarySignatories = {
    name: string;
    socialSecurityNumber: string;
    signerType: string;
    status: string;
    signDate?: string;
};

export type ComplianceSummaryPaymentInformation = {
    bankName: string;
    paymentMethod: string;
    paymentMethodData: string;
};

export type ComplianceSummary = {
    status: CustomerStatusInfo;
    signatories: ComplianceSummarySignatories[];
    paymentInformation: ComplianceSummaryPaymentInformation;
};

export type ActivateErpIntegrationInputResource = {
    activationCode?: string;
    providerId: ErpIntegrationProviderIdResource;
};

export type BannerDataResource = {
    numberOfInvoicesWithIncorrectOrigin: number;
};

export enum Banner {
    Acconomy = "Acconomy",
    BusinessLoans = "BusinessLoans",
    Fortnox = "Fortnox",
    UseIntegrationReminderAcconomy = "UseIntegrationReminderAcconomy",
    UseIntegrationReminderFortnox = "UseIntegrationReminderFortnox",
}

export type BannerResource = {
    name: Banner;
    data?: BannerDataResource;
};

export type DeleteErpIntegrationInputResource = {
    providerId: ErpIntegrationProviderIdResource;
};

export enum ErpIntegrationErrorTypeResource {
    Undefined = "Undefined",
    FortnoxCashMethodInvalidStock = "FortnoxCashMethodInvalidStock",
    FortnoxMissingPermissions = "FortnoxMissingPermissions",
}

export type FortnoxCashMethodInvalidStockDataResource = {
    invoiceNumber: number;
};

export type ErpIntegrationErrorResource<TData = null> = {
    data: TData;
    type: ErpIntegrationErrorTypeResource;
};

export type ErpIntegrationInvoiceImportInputResource = {
    invoiceIds: number[];
    providerId: ErpIntegrationProviderIdResource;
};

export type ErpIntegrationInvoiceImportResource = {
    numberOfInvoices: number;
    totalAmount: number;
};

export type ErpIntegrationInvoiceResource = {
    amount: number;
    containErrors: boolean;
    currency: string;
    customerName: string;
    electronic?: boolean;
    errorDescriptions: string[];
    errorMessage: string;
    invoiceDate: string;
    invoiceDueDate: string;
    invoiceId: number;
    normalizedAmount: number;
};

export type ErpIntegrationInvoiceSummaryResource = {
    estimatedLoadTime?: number;
    normalizedAmount: number;
    numberOfInvoices: number;
    totalNumberOfInvoices: number;
};

export enum ErpIntegrationActivationTypeResource {
    Undefined = "Undefined",
    OAuth = "OAuth",
    Other = "Other",
}

export type ErpIntegrationFeatureResource = {
    invoiceImport: boolean;
    invoiceSummary: boolean;
    singleSignOn: boolean;
};

export type ErpIntegrationOAuthResource = {
    reactivationRequired: boolean;
    url: string;
};

export enum ErpIntegrationProviderIdResource {
    Undefined = "Undefined",
    Acconomy = "Acconomy",
    Fortnox = "Fortnox",
}

export type ErpIntegrationResource = {
    activationType: ErpIntegrationActivationTypeResource;
    feature: ErpIntegrationFeatureResource;
    oAuth: ErpIntegrationOAuthResource;
    providerId: ErpIntegrationProviderIdResource;
    providerName: string;
};

export type GenerateSsoLinkInputResource = {
    path?: string;
    providerId: ErpIntegrationProviderIdResource;
};

export type AnnualKycFollowUpAnswerResource = {
    hasBeneficialOwnersChanged: boolean;
    hasBoardMembersChanged: boolean;
    hasPepChanged: boolean;
};

export type InvoiceConnectionResource = {
    invoiceId: number;
    invoiceNumber: number;
};

export type InvoiceDataResource = {
    branch: string;
    branchAddress: string;
    branchCity: string;
    branchCOAddress: string;
    branchCountry: string;
    branchZipCode: string;
    buyingPercentage: number;
    buyingRate: number;
    commission: number;
    currencyCode: string;
    defaultCurrencyCode: string;
    defaultTotalAmount: number;
    debtor: string;
    debtorAddress: string;
    debtorCity: string;
    debtorCOAddress: string;
    debtorCountry: string;
    debtorId: number;
    debtorZipCode: string;
    dueDate: string;
    fileExists: boolean;
    id: number;
    invoiceDate: string;
    invoiceGroupId: number;
    isCreditInvoice: boolean;
    monitoringReason?: string;
    number: string;
    ocrNumber: string;
    organizationNumber: string;
    payedAmount: number;
    remainingAmount: number;
    sequenceNumber: number;
    service: string;
    serviceId: number;
    serviceIdTranslationTag:
        | "tjansterTjanstid0"
        | "tjansterTjanstid1"
        | "tjansterTjanstid2"
        | "tjansterTjanstid3"
        | "tjansterTjanstid4";
    status:
        | "rejected"
        | "confirmationSought"
        | "duringProcessing"
        | "beforeCertificate"
        | "completed"
        | "settled"
        | "fullyPaid"
        | "legalAction"
        | "collection"
        | "reminder"
        | "unPaid"
        | "fakturastatus2Fakturastatus2id0"
        | "fakturastatus2Fakturastatus2id1"
        | "fakturastatus2Fakturastatus2id2"
        | "fakturastatus2Fakturastatus2id3"
        | "fakturastatus2Fakturastatus2id4"
        | "fakturastatus2Fakturastatus2id5"
        | "fakturastatus2Fakturastatus2id6"
        | "fakturastatus2Fakturastatus2id7"
        | "fakturastatus2Fakturastatus2id8"
        | "fakturastatus2Fakturastatus2id9"
        | "fakturastatus2Fakturastatus2id10"
        | "fakturastatus2Fakturastatus2id11"
        | "fakturastatus2Fakturastatus2id12"
        | "fakturastatus2Fakturastatus2id13"
        | "fakturastatus2Fakturastatus2id14"
        | "fakturastatus2Fakturastatus2id15";
    statusDate: string;
    statusId: number;
    totalAmount: number;
};

export type InvoiceDetailResource = {
    connectedInvoices: InvoiceConnectionResource[];
    data: InvoiceDataResource;
    payments: InvoicePaymentResource[];
    timeline: InvoiceTimelineResource[];
};

export type InvoicePaymentResource = {
    amount: number;
    costs: number;
    currencyCode: string;
    depositionGroupId: number;
    interest: number;
    invoiceGroupId: number;
    paymentMeans: string;
    paymentMeansId: number;
    paymentMeansIdTranslationTag: string;
    registeredDate: string;
};

export type InvoiceTimelineResource = {
    date?: string;
    exception: boolean;
    fulfilled: boolean;
    key: string;
    serious: boolean;
};

export type Nothing = null | undefined;

export type PaymentSummary = {
    number: number;
    totalAmount: number;
};

export type WebApiError<TData = {}> = Error & {
    data: TData;
    response: Response;
};

export type ApiInvoiceGroupInvoice = {
    commissionAmount: number;
    commissionPercentage: number;
    currencyCode: string;
    debtor: string;
    defaultCurrencyCode: string;
    defaultTotalAmount: number;
    id: string;
    number: string;
    monitoringReason?: string;
    serviceId: number;
    totalAmount: number;
};

export type ApiInvoiceGroups = {
    financialTransactions: any[]; // TODO (later) fix type
    invoices: any[]; // TODO (later) fix type
    services: any[]; // TODO (later) fix type
    summary: any; // TODO (later) fix type
    vatDetail: ApiVatDetail;
};

export type ApiVatDetail = {
    vat: number;
    vatProvision: number;
};
